import cn from 'classnames';
import { useRouter } from 'next/router';
import type { FC } from 'react';
import { memo, useCallback } from 'react';

import { Layout } from '@sravni/react-design-system';

import { usePageContext } from '@src/config/PageContext';
import { BANNER_LIST_POSITION_INDEX, BANNER_TYPE } from '@src/constants/banners';
import { getBanners } from '@src/helpers/banners';
import { isNaKartuPage } from '@src/helpers/isNaKartuPage';
import type { IFilter } from '@src/hooks/useFilters';
import { useBanners } from '@src/reducers/banners';
import { useSelectCreditsList } from '@src/reducers/microcredits';
import {
    specOffersLandingPageRouteWebbankirWb1,
    specOffersLandingPageRouteWebbankirWb2,
} from '@src/server/routes/constants';

import Card from './components/Card';
import CardV2 from './components/CardV2';
import { MessageBlock } from './components/MessageBlock';
import Preloader from './components/Preloader';
import styles from './styles.module.scss';
import { getKey } from './utils/getKey';
import { getEmptyProductsTexts, getErrorText } from './utils/getMessageBlockTexts';

interface IProps {
    className?: string;
    preloadTotal?: number;
    filter: IFilter;
}

const ProductList: FC<IProps> = memo((props: IProps) => {
    const { className, preloadTotal = 16 } = props;
    const { withBanners, listKey } = usePageContext();
    const banners = useBanners();
    const { error, items = [], loading, loadingMore } = useSelectCreditsList(listKey);

    const { asPath } = useRouter();

    const [pathWithoutQuery] = asPath.split('?');
    const isNewCardDesign = isNaKartuPage(pathWithoutQuery);

    const CardComponent = memo(isNewCardDesign ? CardV2 : Card);
    const isOneColumn =
        isNewCardDesign ||
        [specOffersLandingPageRouteWebbankirWb1, specOffersLandingPageRouteWebbankirWb2].includes(pathWithoutQuery);

    const renderSlot = useCallback(
        (index, listPosition = BANNER_LIST_POSITION_INDEX) =>
            withBanners && getBanners({ index, banners, listPosition }),
        [withBanners, banners],
    );

    const renderListContent = useCallback(() => {
        const result = items.reduce((output, item, index) => {
            const slot = renderSlot(index, BANNER_LIST_POSITION_INDEX);
            if (slot) {
                output.push(
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={`${banners[BANNER_TYPE].id}-${index}`} className={styles.banner}>
                        {slot}
                    </div>,
                );
            }

            const key = getKey(item._id, index, listKey);
            output.push(<CardComponent key={`p-item-${key}`} offer={item} position={index} />);

            return output;
        }, [] as React.ReactElement[]);

        return result;
    }, [items, renderSlot, listKey, CardComponent, banners]);

    const renderList = useCallback(
        () => (
            <>
                <div
                    className={cn(styles.list, {
                        oneColumn: isOneColumn,
                    })}
                >
                    {renderListContent()}
                </div>
                {loadingMore && (
                    <Preloader className={cn(styles.list, { oneColumn: isOneColumn })} itemsCount={preloadTotal} />
                )}
            </>
        ),
        [isOneColumn, renderListContent, loadingMore, preloadTotal],
    );

    if (error) {
        return <MessageBlock {...getErrorText()} />;
    }

    const renderContent = () => {
        if (!items.length && !loading) {
            <MessageBlock {...getEmptyProductsTexts(listKey)} />;
        }
        return loading ? (
            <Preloader className={cn(styles.list, { oneColumn: isOneColumn })} itemsCount={preloadTotal} />
        ) : (
            renderList()
        );
    };

    return (
        <div className={cn(styles.container, className)}>
            <Layout className={styles.layout}>{renderContent()}</Layout>
        </div>
    );
});

export default ProductList;
